import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import type { Dispute } from "@/types/dispute";

import { useInitReturnPayment } from "@/api/dispute-query";
import { usePrivateAxios } from "@/api/use-private-axios";
import { useVerifyVfdSession } from "@/api/wallet-query";
import Countdown from "@/components/dashboard/countdown";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency } from "@/lib/utils";
import useAuthStore from "@/store/auth";
import { usePaystackStore } from "@/store/paystack";
import { useSafeHaven } from "@/store/safehaven";
import { useVPaymentWidget } from "@/store/vfd";
import { PaymentProvider } from "@/types/banks";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const returnOrderSchema = z.object({
	paymentOption: z.nativeEnum(PaymentProvider, {
		required_error: "Please select a payment method"
	})
});

type ReturnOrderFormValues = z.infer<typeof returnOrderSchema>;

function useVerifyReturnOrder() {
	const privateAxios = usePrivateAxios();
	return useMutation({
		mutationFn: async (data: {
			reference: string;
			paymentProvider: string;
		}) => {
			const response = await privateAxios.post<{
				message: string;
			}>("/order/verify-return-order-payment", data);
			return response.data;
		}
	});
}

function useReturnOrderTranx() {
	const safeHaven = useSafeHaven();
	const paystack = usePaystackStore();
	const queryClient = useQueryClient();
	const vfd = useVPaymentWidget();

	const verifyVfdSession = useVerifyVfdSession();

	const verifyReturnOrder = useVerifyReturnOrder();
	const user = useAuthStore(state => state.user);

	const returnOrder = useInitReturnPayment(async ({ data }, vars) => {
		switch (vars.paymentOption) {
			case PaymentProvider.PAYSTACK:
				paystack.initializePayment({
					amount: (vars.amount * 100) + ((Number(data?.transactionFee) ?? 0) * 100),
					email: user?.email ?? "",
					reference: data?.returnOrder.paymentRef ?? "",
					key: data?.paymentConfig.publicKey ?? "",
					metadata: {
						custom_fields: [
							{
								display_name: user?.fullName ?? "",
								value: user?.fullName ?? "",
								variable_name: user?.fullName ?? ""
							}
						]
					},
					onSuccess() {
						verifyReturnOrder.mutateAsync({
							reference: data?.returnOrder.paymentRef ?? "",
							paymentProvider: vars.paymentOption
						});
					},
					onError() {
						toast.error("Payment failed");
					},
					onCancel() {
						toast.warning("Payment cancelled");
					}
				});

				break;
			case PaymentProvider.VFD:
				vfd.initializePayment({
					amount: vars.amount + (data?.transactionFee ?? 0),
					token: data?.paymentConfig.accessToken ?? "",
					merchantId: data?.paymentConfig.merchantId ?? "",
					onSuccess: async (response) => {
						verifyVfdSession.mutateAsync({
							type: "TRANSACTION",
							transactionId: vars.orderId,
							sessionId: response.reference
						}).then(() => {
							verifyReturnOrder.mutateAsync({
								reference: data?.paymentConfig.reference ?? "",
								paymentProvider: vars.paymentOption
							});
						});
						queryClient.invalidateQueries({ queryKey: ["view-wallet"] });
					},
					onClose() {
						toast.warning("Payment cancelled");
					}
				});

				break;
			case PaymentProvider.SAFEHAVEN:
				if (!user)
					throw new Error("User not found");
				await safeHaven.initializePayment({
					customer: {
						firstName: user.firstName,
						lastName: user.lastName,
						emailAddress: user.email,
						phoneNumber: user.phoneNumber ?? "+234 723 432 3434"
					},
					clientId: data?.paymentConfig.clientId ?? "",
					settlementAccount:
						{
							accountNumber: "0118030405",
							bankCode: "999240"
						},
					amount: vars.amount + (data?.transactionFee ?? 0),
					referenceCode: data?.paymentConfig.reference ?? "",
					callback() {
						queryClient.invalidateQueries({ queryKey: ["view-wallet"] });
					}
				});
				break;
			case PaymentProvider.WALLET:
				verifyReturnOrder.mutateAsync({
					reference: data?.returnOrder.paymentRef ?? "",
					paymentProvider: vars.paymentOption
				}).then(() => {
					toast.success("Payment successful");
				}).catch((error) => {
					toast.error("Payment failed", { description: error.response.data.message });
				}).finally(() => {
					queryClient.invalidateQueries({ queryKey: ["view-wallet"] });
				});
				break;

			default:
				throw new Error("Invalid payment provider");
		}
	}

	);

	const returnOrderInit = async (v: {
		amount: number;
		orderId: string;
		newRef: boolean;
		paymentOption: PaymentProvider;
	}) => {
		if (!user)
			throw new Error("User not found");
		await returnOrder.mutateAsync(v);
	};

	return {
		query: returnOrder,
		returnOrderInit
	};
}

export function ReturnOrder({ dispute }: { dispute: Dispute }) {
	const form = useForm<ReturnOrderFormValues>({
		resolver: zodResolver(returnOrderSchema),
		defaultValues: {
			paymentOption: undefined
		}
	});

	const returnOrder = useReturnOrderTranx();
	const [secondsLeft, setSecondsLeft] = useState(0);

	useEffect(() => {
		if (dispute?.updatedAt) {
			const deadline = new Date(new Date(dispute.updatedAt).getTime() + (3 * 24 * 60 * 60 * 1000));
			const diff = deadline.getTime() - new Date().getTime();
			setSecondsLeft(Math.floor(diff / 1000));
		}
	}, [dispute?.updatedAt]);

	const onSubmit = async (values: ReturnOrderFormValues) => {
		await returnOrder.returnOrderInit({
			amount: dispute.returnFee ?? 0,
			newRef: true,
			orderId: dispute.order.id,
			paymentOption: values.paymentOption
		});
	};

	return (
		<Dialog>
			<DialogTrigger asChild>
				<Button variant="outline">Return order</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle>Return order</DialogTitle>
					<DialogDescription>
						You have 3 days to complete this return process. A return processing fee will be charged.
					</DialogDescription>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
						<div className="space-y-4">
							<div className="bg-muted p-4 rounded-lg">
								<div className="text-center space-y-2">
									<div className="text-2xl font-bold text-primary">
										{formatCurrency(dispute.returnFee ?? 0, "NGN")}
									</div>
									<div className="text-sm text-muted-foreground">
										Return processing fee
									</div>
								</div>
							</div>
							<div className="flex items-center justify-between text-sm">
								<span className="text-muted-foreground">Time remaining:</span>
								<span className="bg-green-500 text-white px-3 py-1 rounded-md">
									<Countdown seconds={secondsLeft} />
								</span>
							</div>
						</div>
						<FormField
							control={form.control}
							name="paymentOption"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Select payment method</FormLabel>
									<Select onValueChange={field.onChange} value={field.value}>
										<FormControl>
											<SelectTrigger>
												<SelectValue placeholder="Choose how to pay" />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											<SelectItem value={PaymentProvider.PAYSTACK}>Paystack</SelectItem>
											<SelectItem value={PaymentProvider.SAFEHAVEN}>Safe Haven</SelectItem>
											<SelectItem value={PaymentProvider.WALLET}>Wallet</SelectItem>
											<SelectItem value={PaymentProvider.VFD}>VFD</SelectItem>
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>
						<DialogFooter>
							<Button type="submit" className="w-full">Pay return fee</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
}
